import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { leaveAPI } from '../../api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Calendar } from 'lucide-react';

const LeaveTypesManagement = () => {
  const { user } = useAuth();
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [newType, setNewType] = useState({
    name: '',
    code: '',
    days_per_year: 15,
    is_paid: true,
    requires_approval: true
  });

  const fetchLeaveTypes = useCallback(async () => {
    if (!user?.company_id) return;
    try {
      const response = await leaveAPI.getTypes(user.company_id);
      setLeaveTypes(response.data);
    } catch (err) {
      toast.error('Failed to load leave types');
    } finally {
      setLoading(false);
    }
  }, [user?.company_id]);

  useEffect(() => {
    fetchLeaveTypes();
  }, [fetchLeaveTypes]);

  const handleAddType = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...newType,
        days_per_year: parseInt(newType.days_per_year)
      };
      await leaveAPI.createType(user.company_id, data);
      toast.success('Leave type created');
      setIsAddOpen(false);
      setNewType({
        name: '',
        code: '',
        days_per_year: 15,
        is_paid: true,
        requires_approval: true
      });
      fetchLeaveTypes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create leave type');
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Leave Types">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout 
      title="Leave Types" 
      subtitle="Configure leave categories for your company"
    >
      {/* Add Button */}
      <div className="flex justify-end mb-6">
        <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="add-leave-type-btn">
              <Plus className="h-4 w-4" />
              Add Leave Type
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Create Leave Type</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddType} className="space-y-4">
              <div className="space-y-2">
                <Label>Leave Name</Label>
                <Input
                  value={newType.name}
                  onChange={(e) => setNewType({...newType, name: e.target.value})}
                  placeholder="e.g. Vacation Leave"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Code</Label>
                  <Input
                    value={newType.code}
                    onChange={(e) => setNewType({...newType, code: e.target.value})}
                    placeholder="e.g. VL"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Days per Year</Label>
                  <Input
                    type="number"
                    value={newType.days_per_year}
                    onChange={(e) => setNewType({...newType, days_per_year: e.target.value})}
                    required
                  />
                </div>
              </div>
              <div className="flex items-center justify-between p-3 bg-secondary rounded-lg">
                <div>
                  <p className="font-medium text-sm">Paid Leave</p>
                  <p className="text-xs text-muted-foreground">Employee receives salary during leave</p>
                </div>
                <Switch
                  checked={newType.is_paid}
                  onCheckedChange={(checked) => setNewType({...newType, is_paid: checked})}
                />
              </div>
              <div className="flex items-center justify-between p-3 bg-secondary rounded-lg">
                <div>
                  <p className="font-medium text-sm">Requires Approval</p>
                  <p className="text-xs text-muted-foreground">Manager must approve leave request</p>
                </div>
                <Switch
                  checked={newType.requires_approval}
                  onCheckedChange={(checked) => setNewType({...newType, requires_approval: checked})}
                />
              </div>
              <Button type="submit" className="w-full">Create Leave Type</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Leave Types Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Leave Type</TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Days/Year</TableHead>
                <TableHead>Paid</TableHead>
                <TableHead>Requires Approval</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {leaveTypes.map((type) => (
                <TableRow key={type.id} data-testid={`leave-type-row-${type.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-blue-500" />
                      <span className="font-medium">{type.name}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <code className="text-xs bg-muted px-2 py-1 rounded">{type.code}</code>
                  </TableCell>
                  <TableCell>{type.days_per_year} days</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={type.is_paid ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-50'}>
                      {type.is_paid ? 'Paid' : 'Unpaid'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={type.requires_approval ? 'bg-amber-50 text-amber-700' : 'bg-gray-50'}>
                      {type.requires_approval ? 'Yes' : 'No'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className={type.is_active ? 'status-approved' : 'status-rejected'}>
                      {type.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {leaveTypes.length === 0 && (
            <div className="text-center py-12">
              <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No leave types configured</p>
            </div>
          )}
        </CardContent>
      </Card>
    </DashboardLayout>
  );
};

export default LeaveTypesManagement;
